import { useState, useEffect } from 'react';
import { usePagination } from '@/shared/hooks/usePagination';
import { Pagination } from '@/shared/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useClients, Client } from '@/features/clients/hooks/useClients';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Pencil, Archive, Mail, Phone, FileText, Calendar, UserRound, ArrowUpDown, LayoutGrid, List } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { ClientFormDialog, NewClientData } from '@/features/clients/components/ClientFormDialog';

const localeMap = { fr, en: enUS, es };


export default function ClientsPage() {
  const { language } = useLanguage();
  const { clients, isLoading, createClient, archiveClient } = useClients();
  const { toast } = useToast();

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  type SortKey = 'name-asc' | 'name-desc' | 'first-asc' | 'created-desc' | 'sessions-desc';
  const [sortBy, setSortBy] = useState<SortKey>(
    () => (localStorage.getItem('clients_sort_preference') as SortKey) ?? 'created-desc'
  );
  const handleSortChange = (v: SortKey) => {
    setSortBy(v);
    localStorage.setItem('clients_sort_preference', v);
  };
  type ViewMode = 'grid' | 'list';
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem('clients_view_preference') as ViewMode) ?? 'grid'
  );
  const handleViewChange = (v: ViewMode) => {
    setViewMode(v);
    localStorage.setItem('clients_view_preference', v);
  };
  const [dialogOpen, setDialogOpen] = useState(false);

  const t = {
    fr: { title: 'Clients', search: 'Rechercher un client...', add: 'Ajouter', save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer', since: 'Depuis', contracts: 'contrats', noClients: 'Aucun client trouvé', name: 'Nom', email: 'Email', phone: 'Téléphone', birthDate: 'Date de naissance', notes: 'Notes', newClient: 'Nouveau client', editClient: 'Modifier le client', totalClients: 'Total clients', totalContracts: 'Total contrats' },
    en: { title: 'Clients', search: 'Search clients...', add: 'Add', save: 'Save', cancel: 'Cancel', delete: 'Delete', since: 'Since', contracts: 'contracts', noClients: 'No clients found', name: 'Name', email: 'Email', phone: 'Phone', birthDate: 'Date of birth', notes: 'Notes', newClient: 'New Client', editClient: 'Edit Client', totalClients: 'Total clients', totalContracts: 'Total contracts' },
    es: { title: 'Clientes', search: 'Buscar clientes...', add: 'Agregar', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', since: 'Desde', contracts: 'contratos', noClients: 'No se encontraron clientes', name: 'Nombre', email: 'Correo', phone: 'Teléfono', birthDate: 'Fecha de nacimiento', notes: 'Notas', newClient: 'Nuevo cliente', editClient: 'Editar cliente', totalClients: 'Total clientes', totalContracts: 'Total contratos' },
  }[language];
  const locale = localeMap[language];

  const filtered = clients
    .filter(c => [c.name, c.email, c.phone].some(v => v?.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':     return a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name);
        case 'name-desc':    return b.last_name.localeCompare(a.last_name) || b.first_name.localeCompare(a.first_name);
        case 'first-asc':    return a.first_name.localeCompare(b.first_name);
        case 'sessions-desc': return b.contract_count - a.contract_count;
        case 'created-desc':
        default:             return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
  const totalContracts = clients.reduce((sum, c) => sum + c.contract_count, 0);
  const pagination = usePagination(filtered, 20);

  useEffect(() => { pagination.reset(); }, [search, sortBy]);

  const openNew = () => { setDialogOpen(true); };

  useEffect(() => {
    const handler = () => openNew();
    window.addEventListener('open-new-client-dialog', handler);
    return () => window.removeEventListener('open-new-client-dialog', handler);
  }, []);
  const openEdit = (c: Client) => {
    navigate(`/clients/${c.id}?edit=true`);
  };

  const handleCreateClient = async (data: NewClientData) => {
    try {
      await createClient.mutateAsync(data as any);
      setDialogOpen(false);
      toast({ title: 'Client créé ✓' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveClient.mutateAsync(id);
      toast({ title: 'Client archivé ✓' });
    } catch (err: any) {
      toast({ title: 'Erreur', description: err?.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold font-display text-foreground">{t.title}</h1>
          <p className="text-muted-foreground mt-1">{clients.length} {t.totalClients} · {totalContracts} {t.contracts}</p>
        </div>
        <Button onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus size={16} /> {t.add}
        </Button>
      </motion.div>

      {/* Search + Sort */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input placeholder={t.search} value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-secondary/50 border-border/50 h-11" />
        </div>
        <Select value={sortBy} onValueChange={(v) => handleSortChange(v as SortKey)}>
          <SelectTrigger className="w-auto h-11 px-3 gap-2 bg-secondary/50 border-border/50 shrink-0">
            <ArrowUpDown size={14} className="text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created-desc">Date de création</SelectItem>
            <SelectItem value="name-asc">Nom A → Z</SelectItem>
            <SelectItem value="name-desc">Nom Z → A</SelectItem>
            <SelectItem value="first-asc">Prénom A → Z</SelectItem>
            <SelectItem value="sessions-desc">Nb contrats ↓</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border border-border/50 rounded-lg overflow-hidden shrink-0">
          <button
            className={`h-11 w-10 flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}
            onClick={() => handleViewChange('grid')}
            title="Vue grille"
          >
            <LayoutGrid size={15} />
          </button>
          <button
            className={`h-11 w-10 flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}
            onClick={() => handleViewChange('list')}
            title="Vue liste"
          >
            <List size={15} />
          </button>
        </div>
      </motion.div>

      {/* Grid / List */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 rounded-2xl bg-secondary/50" />)}
        </div>
      ) : filtered.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-20 text-muted-foreground"
        >
          <UserRound size={56} className="mx-auto mb-4 opacity-20" />
          <p className="text-lg">{t.noClients}</p>
          <Button onClick={openNew} variant="secondary" className="mt-4 gap-2">
            <Plus size={16} /> {t.add}
          </Button>
        </motion.div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pagination.paged.map((client, i) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="glass-card-hover p-5 space-y-4 group cursor-pointer"
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{client.name}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Calendar size={10} /> {t.since} {format(new Date(client.created_at), 'MMM yyyy', { locale })}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); openEdit(client); }}>
                    <Pencil size={14} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-amber-500" onClick={(e) => e.stopPropagation()}>
                        <Archive size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Archiver ce client ?</AlertDialogTitle>
                        <AlertDialogDescription>Le client sera masqué de la liste mais ses données seront conservées.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleArchive(client.id)}>Archiver</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                {client.email && (
                  <p className="text-muted-foreground flex items-center gap-2">
                    <Mail size={13} className="text-primary/60" /> {client.email}
                  </p>
                )}
                {client.phone && (
                  <p className="text-muted-foreground flex items-center gap-2">
                    <Phone size={13} className="text-primary/60" /> {client.phone}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
                  <FileText size={11} /> {client.contract_count} {t.contracts}
                </span>
              </div>
            </motion.div>
          ))}
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={20}
            onPage={pagination.setPage}
          />
        </div>
      ) : (
        /* ── Vue liste compacte ── */
        <div className="divide-y rounded-2xl border border-border/40 overflow-hidden">
          {pagination.paged.map((client) => (
            <div
              key={client.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/30 cursor-pointer group transition-colors"
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                {client.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-[1fr_1fr_1fr] gap-x-4 items-center">
                <p className="font-medium text-sm text-foreground truncate">{client.name}</p>
                <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  {client.email
                    ? <><Mail size={11} className="shrink-0" />{client.email}</>
                    : <span className="italic opacity-40">—</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  {client.phone
                    ? <><Phone size={11} className="shrink-0" />{client.phone}</>
                    : <span className="italic opacity-40">—</span>}
                </p>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium shrink-0">
                {client.contract_count} contrats
              </span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); openEdit(client); }}>
                  <Pencil size={13} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-amber-500" onClick={(e) => e.stopPropagation()}>
                      <Archive size={13} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Archiver ce client ?</AlertDialogTitle>
                      <AlertDialogDescription>Le client sera masqué de la liste mais ses données seront conservées.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleArchive(client.id)}>Archiver</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
          <div className="px-4 py-2 bg-secondary/20">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={20}
              onPage={pagination.setPage}
            />
          </div>
        </div>
      )}

      {/* Dialog */}
      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={null}
        onCreateClient={handleCreateClient}
      />
    </div>
  );
}
